/**
 * Email Flow Configuration Utility Functions
 * Contains helper functions for parsing and transforming email template data
 */
import { FROM_OPTIONS, RECIPIENT_TYPES, SUBJECT_PLACEHOLDERS } from "./emailFlowConfig.constants";

/**
 * Parse sender string from API to find matching fromOption
 * @param {string} sender - e.g., "@noReplyEmail" or "@workflowEmailSystem"
 * @returns {Object} matching fromOption or default (No Reply Email)
 */
export const parseSender = (sender) => {
  if (!sender) return FROM_OPTIONS[0];
  const match = FROM_OPTIONS.find((opt) => opt.value === sender);
  return match || FROM_OPTIONS[0];
};

/**
 * Parse receivers array from API to recipients format used in component
 * @param {Array} receivers - Array of receiver objects from API
 * @returns {Array} recipients in component format with proper Id generation
 */
export const parseReceivers = (receivers) => {
  if (!receivers || !Array.isArray(receivers)) return [];
  return receivers.map((receiver, index) => {
    let recipientId;
    if (receiver.type === RECIPIENT_TYPES.SYSTEM) {
      // For system recipients, the email contains the id (e.g., @creatorDrutasEmail)
      recipientId = receiver.email;
    } else if (receiver.type === RECIPIENT_TYPES.MANUAL) {
      recipientId = `manual-${index}-${Date.now()}`;
    } else {
      recipientId = receiver.id || `recipient-${index}-${Date.now()}`;
    }
    return {
      Name: receiver.name,
      Email: receiver.email,
      TemporaryContact: null,
      PhotoLink: null,
      PhotoLinks: null,
      Id: recipientId,
      Type: receiver.type,
    };
  });
};

/**
 * Parse subject string to extract pills (@refId, @taskId) and text segments
 * @param {string} subject - e.g., "helloo@refIdtest@taskId"
 * @returns {Object} { segments: Array, remainingText: string }
 */
export const parseSubject = (subject) => {
  if (!subject) return { segments: [], remainingText: "" };
  const segments = [];
  const pillRegex = /(@refId|@taskId)/g;
  let lastIndex = 0;
  let match;
  while ((match = pillRegex.exec(subject)) !== null) {
    if (match.index > lastIndex) {
      const textBefore = subject.substring(lastIndex, match.index);
      if (textBefore) {
        segments.push({ type: "text", value: textBefore });
      }
    }
    const pillValue = match[1];
    const pillPlaceholder = SUBJECT_PLACEHOLDERS.find((p) => p.value === pillValue);
    segments.push({
      type: "pill",
      value: pillValue,
      label: pillPlaceholder?.label || pillValue.replace("@", ""),
    });

    lastIndex = match.index + match[0].length;
  }
  const remainingText = subject.substring(lastIndex);
  return { segments, remainingText };
};

/**
 * Get CSS class for recipient chip based on type
 * @param {string} type - Recipient type (system, manual, user, workflow)
 * @returns {string} CSS class name for the chip
 */
export const getRecipientChipClass = (type) => {
  const baseClass = "email-flow-chip";

  switch (type) {
    case RECIPIENT_TYPES.SYSTEM:
      return `${baseClass} email-flow-chip-system`;
    case RECIPIENT_TYPES.MANUAL:
      return `${baseClass} email-flow-chip-manual`;
    case RECIPIENT_TYPES.USER:
      return `${baseClass} email-flow-chip-user`;
    case RECIPIENT_TYPES.WORKFLOW:
      return `${baseClass} email-flow-chip-workflow`;
    default:
      return baseClass;
  }
};

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean} True if valid email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Get initials from name for avatar display
 * @param {string} name - Name to extract initials from
 * @returns {string} Initials (max 2 characters)
 */
export const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Avatar text for predefined system rows in TO/CC dropdowns (not contact search rows).
 * CR = Creator, CA = Current Assignee, CL1/CL2 = Creator Manager L1/L2.
 *
 * @param {{ id?: string, label?: string }} option
 * @returns {string}
 */
export const getSystemRecipientOptionInitials = (option) => {
  const id = option?.id;
  if (id === "@creatorDrutasEmail" || id === "@creatorSSOEmail") return "CR";
  if (id === "@currentAssigneeDrutasEmail" || id === "@currentAssigneeSSOEmail") return "CA";
  if (id === "@creatorL1Manager") return "CL1";
  if (id === "@creatorL2Manager") return "CL2";
  return getInitials(option?.label);
};
